import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { getMyEmployee } from "../../api/employees";
import {
  appBreakIn,
  appBreakOut,
  appCheckIn,
  appCheckOut,
  getTodayAttendance,
} from "../../api/attendance";
import { getWfhRequests } from "../../api/wfh";
import { normalizeWorkMode } from "../../utils/workModeHelpers";
import { employeeRequiresShiftHandover } from "../../utils/orgHelpers";
import {
  formatGpsLabel,
  getCurrentGpsPosition,
} from "../../utils/geolocation";
import {
  attendanceRecordDateKey,
  cairoCalendarDateKey,
  formatAttendanceTime,
  isOpenPreviousAttendanceDay,
} from "../../utils/attendanceTimeFormat";
import {
  getShiftLocationTrackingStatus,
  openShiftLocationSettings,
  syncShiftTrackingWithSession,
} from "../../utils/shiftLocationTracker";
import { isNative } from "../../utils/mobile";
import "./RemoteAttendance.css";

const WORK_MODE_LABELS = {
  OFFICE: "مكتب (بصمة)",
  REMOTE: "عن بُعد دائم",
  HYBRID: "مختلط (مكتب + عمل من المنزل)",
};

function localDateKey(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  return localDateKey(value);
}

function formatDateAr(value) {
  const key = toDateKey(value);
  if (!key) return "—";
  try {
    return new Date(`${key}T12:00:00`).toLocaleDateString("ar-EG", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return key;
  }
}

function findApprovedWfhForDay(requests, dayKey) {
  if (!dayKey || !Array.isArray(requests)) return null;
  return (
    requests.find((req) => {
      if (req.status !== "APPROVED") return false;
      const from = toDateKey(req.dateFrom);
      const to = toDateKey(req.dateTo || req.dateFrom);
      return from && to && dayKey >= from && dayKey <= to;
    }) || null
  );
}

function RemoteAttendance() {
  const { user } = useAuth();
  const { employeesByBranch } = useData();
  const profile = useMemo(() => {
    const all = Object.values(employeesByBranch || {}).flat();
    return all.find(
      (e) => e.id === user?.employeeId || e.userId === user?.id,
    );
  }, [employeesByBranch, user?.employeeId, user?.id]);
  const [workMode, setWorkMode] = useState(
    normalizeWorkMode(profile?.workMode || user?.workMode),
  );
  const [today, setToday] = useState(null);
  const [handover, setHandover] = useState(null);
  const [scheduleExpectedPunches, setScheduleExpectedPunches] = useState(2);
  const [allowPermitMiddayPunches, setAllowPermitMiddayPunches] =
    useState(false);
  const [approvedWfhToday, setApprovedWfhToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastGps, setLastGps] = useState(null);
  const [trackStatus, setTrackStatus] = useState(null);

  useEffect(() => {
    if (profile?.workMode) setWorkMode(normalizeWorkMode(profile.workMode));
  }, [profile?.workMode]);

  const refreshTrackStatus = useCallback(async () => {
    if (!isNative()) {
      setTrackStatus(null);
      return;
    }
    const status = await getShiftLocationTrackingStatus();
    setTrackStatus(status);
  }, []);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const dayKey = localDateKey();
      const [attendance, me, wfhData] = await Promise.all([
        getTodayAttendance(),
        getMyEmployee().catch(() => null),
        getWfhRequests({ status: "APPROVED" }).catch(() => []),
      ]);
      setToday(attendance.today);
      setHandover(attendance.handover || null);
      setAllowPermitMiddayPunches(
        Boolean(attendance.allowPermitMiddayPunches),
      );
      // Prefer class schedule from app-status (not day record elevated to 4).
      const scheduleExpected =
        attendance.expectedPunchCount === 4
          ? 4
          : attendance.expectedPunchCount === 2
            ? 2
            : 2;
      setScheduleExpectedPunches(scheduleExpected);
      const mode = normalizeWorkMode(
        me?.workMode || profile?.workMode || user?.workMode,
      );
      if (me?.workMode) setWorkMode(mode);

      const requests = Array.isArray(wfhData)
        ? wfhData
        : wfhData?.items || [];
      const mine = requests.filter(
        (r) =>
          !me?.id ||
          r.employeeId === me.id ||
          r.employee?.id === me.id ||
          r.employee?.userId === user?.id,
      );
      setApprovedWfhToday(
        mode === "HYBRID" ? findApprovedWfhForDay(mine, dayKey) : null,
      );

      const record = attendance.today;
      if (
        record &&
        Number.isFinite(Number(record.latitude)) &&
        Number.isFinite(Number(record.longitude))
      ) {
        setLastGps({
          latitude: Number(record.latitude),
          longitude: Number(record.longitude),
          accuracy: record.locationAccuracy ?? undefined,
        });
      }

      await syncShiftTrackingWithSession(record);
      await refreshTrackStatus();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تحميل حالة الحضور";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setLoading(false);
    }
  }, [profile?.workMode, refreshTrackStatus, user?.id, user?.workMode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const checkedIn = Boolean(today?.checkInTime);
  const checkedOut = Boolean(today?.checkOutTime);
  const brokeOut = Boolean(today?.breakOutTime);
  const brokeIn = Boolean(today?.breakInTime);
  const expectedPunchCount =
    today?.expectedPunchCount === 4 || today?.expectedPunchCount === 2
      ? today.expectedPunchCount
      : scheduleExpectedPunches;
  const needsFourPunches = expectedPunchCount === 4;
  const showPermitMiddayPunches =
    allowPermitMiddayPunches ||
    (scheduleExpectedPunches === 2 &&
      (brokeOut || brokeIn || expectedPunchCount === 4));
  const showMiddayPunches = needsFourPunches || showPermitMiddayPunches;
  const middayIsPermit =
    showPermitMiddayPunches && scheduleExpectedPunches !== 4;
  const exitPunchLabel = middayIsPermit ? "خروج إذن" : "خروج بريك";
  const returnPunchLabel = middayIsPermit ? "رجوع إذن" : "عودة بريك";
  const shownDate = attendanceRecordDateKey(today?.date) || toDateKey(today?.date);
  const openPreviousDay = isOpenPreviousAttendanceDay(
    today,
    cairoCalendarDateKey(),
  );
  // A closed punch from the previous Egypt day must not disable receiving
  // the shift that starts at midnight.
  const closedPreviousDay =
    checkedOut && Boolean(shownDate) && shownDate < localDateKey();
  const canStartShift = !checkedIn || closedPreviousDay;
  const canBreakOut =
    checkedIn && !checkedOut && !brokeOut && showMiddayPunches;
  const canBreakIn =
    checkedIn && !checkedOut && brokeOut && !brokeIn && showMiddayPunches;
  // Class-4 break schedules always need mid punches before checkout.
  // Permit midday is optional until the employee starts خروج إذن.
  const canCheckOut =
    checkedIn &&
    !checkedOut &&
    (needsFourPunches && !middayIsPermit
      ? brokeOut && brokeIn
      : !brokeOut || brokeIn);
  const handoverRequired =
    Boolean(handover?.required) || employeeRequiresShiftHandover(profile);
  const waitingForIncoming =
    handoverRequired && checkedIn && !checkedOut && handover?.waitingForIncoming;
  const incomingName = handover?.incoming?.fullName;

  useEffect(() => {
    if (!checkedIn || checkedOut || !handoverRequired) return undefined;
    const id = setInterval(() => {
      refresh(true);
    }, 15_000);
    return () => clearInterval(id);
  }, [checkedIn, checkedOut, handoverRequired, refresh]);

  async function captureGps() {
    setLocating(true);
    setError("");
    try {
      const coords = await getCurrentGpsPosition();
      setLastGps(coords);
      return coords;
    } catch (err) {
      setSuccess("");
      setError(err.message || "تعذر تحديد الموقع");
      return null;
    } finally {
      setLocating(false);
    }
  }

  async function handleCheckIn() {
    if (workMode === "HYBRID" && !approvedWfhToday) {
      setError(
        "تسجيل الحضور من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل",
      );
      setSuccess("");
      return;
    }

    const coords = await captureGps();
    if (!coords) return;

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await appCheckIn({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        deviceInfo: navigator.userAgent?.slice(0, 200),
      });
      setToday(result);
      setHandover(result.handover || null);
      setSuccess(
        isNative()
          ? "تم تسجيل الحضور. التتبع الدوري للموقع شغال كل 15 دقيقة أثناء الشيفت."
          : "تم تسجيل الحضور بنجاح مع موقعك الحالي",
      );
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تسجيل الحضور";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setBusy(false);
    }
  }

  async function handleBreakOut() {
    if (workMode === "HYBRID" && !approvedWfhToday) {
      setError(
        middayIsPermit
          ? "تسجيل خروج الإذن من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل"
          : "تسجيل البريك من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل",
      );
      setSuccess("");
      return;
    }

    const coords = await captureGps();
    if (!coords) return;

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await appBreakOut({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        deviceInfo: navigator.userAgent?.slice(0, 200),
      });
      setToday(result);
      setHandover(result.handover || null);
      setSuccess(
        middayIsPermit
          ? "تم تسجيل خروج الإذن بنجاح مع موقعك الحالي"
          : "تم تسجيل خروج البريك بنجاح مع موقعك الحالي",
      );
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        (middayIsPermit
          ? "تعذر تسجيل خروج الإذن"
          : "تعذر تسجيل خروج البريك");
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setBusy(false);
    }
  }

  async function handleBreakIn() {
    if (workMode === "HYBRID" && !approvedWfhToday) {
      setError(
        middayIsPermit
          ? "تسجيل رجوع الإذن من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل"
          : "تسجيل البريك من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل",
      );
      setSuccess("");
      return;
    }

    const coords = await captureGps();
    if (!coords) return;

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await appBreakIn({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        deviceInfo: navigator.userAgent?.slice(0, 200),
      });
      setToday(result);
      setHandover(result.handover || null);
      setSuccess(
        middayIsPermit
          ? "تم تسجيل رجوع الإذن بنجاح مع موقعك الحالي"
          : "تم تسجيل العودة من البريك بنجاح مع موقعك الحالي",
      );
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        (middayIsPermit
          ? "تعذر تسجيل رجوع الإذن"
          : "تعذر تسجيل العودة من البريك");
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setBusy(false);
    }
  }

  async function handleCheckOut() {
    if (workMode === "HYBRID" && !approvedWfhToday) {
      setError(
        "تسجيل الانصراف من التطبيق متاح فقط في الأيام التي وافق عليها الـ HR لطلب العمل من المنزل",
      );
      setSuccess("");
      return;
    }

    const coords = await captureGps();
    if (!coords) return;

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await appCheckOut({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        deviceInfo: navigator.userAgent?.slice(0, 200),
      });
      setToday(result);
      setHandover(result.handover || null);
      setSuccess(
        result?.closedPreviousDay
          ? "تم تسجيل انصراف اليوم السابق. تقدر دلوقتي تسجّل حضور النهاردة"
          : "تم تسجيل الانصراف بنجاح مع موقعك الحالي",
      );
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تسجيل الانصراف";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setBusy(false);
    }
  }

  const isOffice = workMode === "OFFICE";
  const isHybrid = workMode === "HYBRID";
  const isRemote = workMode === "REMOTE";
  const hybridDayAllowed = Boolean(approvedWfhToday);
  const canUseApp = isRemote || (isHybrid && hybridDayAllowed);
  const checkoutBlocked = waitingForIncoming || (handoverRequired && checkedIn && !checkedOut && handover && !handover.canCheckOut);
  const actionsDisabled = busy || locating;
  const punchHint = needsFourPunches
    ? "نظامك 4 بصمات: حضور ← خروج بريك ← عودة بريك ← انصراف. كل بصمة بتلتقط GPS."
    : "عند الضغط على الحضور أو الانصراف سيتم التقاط موقعك الحالي تلقائيًا.";

  return (
    <div className="remote-att-page" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">حضور وانصراف التطبيق</h1>
        <p className="ess-home-subtitle">
          تسجيل الحضور{needsFourPunches ? " والبريك" : ""} والانصراف من التطبيق
          مع التقاط موقعك الحالي (GPS). طلبات العمل من المنزل من الصفحة المنفصلة.
        </p>
      </div>

      <div className="remote-att-card">
        <div className="remote-att-mode">
          <span className="remote-att-mode-label">نظام العمل</span>
          <strong>{WORK_MODE_LABELS[workMode] || workMode}</strong>
        </div>

        {isOffice && (
          <div className="remote-att-banner remote-att-banner-warn">
            نظامك مكتبي (OFFICE) — الحضور يتم عبر جهاز البصمة فقط. لا يمكن
            التسجيل من التطبيق.
          </div>
        )}

        {isHybrid && !loading && hybridDayAllowed && (
          <div className="remote-att-banner remote-att-banner-ok">
            اليوم معتمد للعمل من المنزل ({formatDateAr(approvedWfhToday.dateFrom)}
            {toDateKey(approvedWfhToday.dateTo) !==
              toDateKey(approvedWfhToday.dateFrom)
              ? ` حتى ${formatDateAr(approvedWfhToday.dateTo)}`
              : ""}
            ). يمكنك تسجيل الحضور والانصراف من التطبيق مع GPS.
          </div>
        )}

        {isHybrid && !loading && !hybridDayAllowed && (
          <div className="remote-att-banner remote-att-banner-warn">
            للنظام المختلط: أزرار الحضور والانصراف تفتح فقط في اليوم الذي وافق
            عليه الـ HR من صفحة «العمل من المنزل». باقي الأيام الحضور بالبصمة في
            المكتب.
          </div>
        )}

        {isRemote && (
          <div className="remote-att-banner remote-att-banner-info">
            نظامك عن بُعد دائم — يمكنك تسجيل{" "}
            {needsFourPunches && !middayIsPermit
              ? "الحضور والبريك والانصراف"
              : showPermitMiddayPunches
                ? "الحضور وخروج/رجوع الإذن والانصراف"
                : "الحضور والانصراف"}{" "}
            من التطبيق كل يوم مع التقاط موقع GPS.
          </div>
        )}

        {openPreviousDay && !loading && (
          <div className="remote-att-banner remote-att-banner-warn">
            لسه ما سجلتش انصراف يوم {formatDateAr(shownDate)}. سجّل الانصراف
            الأول، وبعدين تقدر تسجّل حضور النهاردة.
          </div>
        )}

        {handoverRequired && !loading && checkedIn && !checkedOut && checkoutBlocked && (
          <div className="remote-att-banner remote-att-banner-warn">
            لا يمكن تسجيل الانصراف قبل أن تستلم الموديريتور التالية الشيفت.
            انتظري حضورها أولاً.
          </div>
        )}

        {handoverRequired && !loading && checkedIn && !checkedOut && !checkoutBlocked && incomingName && (
          <div className="remote-att-banner remote-att-banner-ok">
            {incomingName} استلمت الشيفت — تقدري تسجلي انصراف حالا.
          </div>
        )}

        {loading ? (
          <p className="remote-att-muted">جاري التحميل...</p>
        ) : (
          <div className="remote-att-status">
            <div className="remote-att-status-row">
              <span>{openPreviousDay ? "حضور اليوم المفتوح" : "حضور اليوم"}</span>
              <strong>{formatAttendanceTime(today?.checkInTime)}</strong>
            </div>
            {showMiddayPunches && (
              <>
                <div className="remote-att-status-row">
                  <span>{exitPunchLabel}</span>
                  <strong>{formatAttendanceTime(today?.breakOutTime)}</strong>
                </div>
                <div className="remote-att-status-row">
                  <span>{returnPunchLabel}</span>
                  <strong>{formatAttendanceTime(today?.breakInTime)}</strong>
                </div>
              </>
            )}
            <div className="remote-att-status-row">
              <span>
                {openPreviousDay ? "انصراف اليوم المفتوح" : "انصراف اليوم"}
              </span>
              <strong>{formatAttendanceTime(today?.checkOutTime)}</strong>
            </div>
            {today?.totalWorkingHours != null && (
              <div className="remote-att-status-row">
                <span>ساعات العمل</span>
                <strong>{Number(today.totalWorkingHours).toFixed(2)}</strong>
              </div>
            )}
            {today?.status && (
              <div className="remote-att-status-row">
                <span>الحالة</span>
                <strong>{today.status}</strong>
              </div>
            )}
            {(lastGps ||
              (Number.isFinite(Number(today?.latitude)) &&
                Number.isFinite(Number(today?.longitude)))) && (
              <div className="remote-att-status-row">
                <span>الموقع (GPS)</span>
                <strong>
                  {formatGpsLabel(
                    lastGps?.latitude ?? today.latitude,
                    lastGps?.longitude ?? today.longitude,
                  )}
                </strong>
              </div>
            )}
          </div>
        )}

        {canUseApp && (
          <>
            <div className="remote-att-gps-hint">
              {locating ? "جاري تحديد الموقع..." : punchHint}
            </div>
            {isNative() && checkedIn && !checkedOut && (
              <div className="remote-att-banner remote-att-banner-info">
                {trackStatus?.state === "paused"
                  ? "التتبع متوقف مؤقتاً أثناء البريك — هيرجع تلقائياً بعد العودة."
                  : trackStatus?.state === "active" || trackStatus?.running
                    ? "تتبع الموقع نشط في الخلفية كل 15 دقيقة حتى لو قفلت التطبيق."
                    : "جاري تفعيل تتبع الموقع أثناء الشيفت…"}
                {trackStatus && trackStatus.backgroundGranted === false && (
                  <div className="remote-att-gps-hint" style={{ marginTop: 8 }}>
                    لتأكيد التتبع والتطبيق مقفول، اختار «السماح طوال الوقت» للموقع.{" "}
                    <button
                      type="button"
                      className="remote-att-btn"
                      style={{ display: "inline", padding: "4px 10px" }}
                      onClick={() => openShiftLocationSettings()}
                    >
                      فتح إعدادات الموقع
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="remote-att-actions">
              <button
                type="button"
                className="remote-att-btn remote-att-btn-in"
                disabled={actionsDisabled || !canStartShift}
                onClick={handleCheckIn}
              >
                {locating && canStartShift
                  ? "جاري تحديد الموقع..."
                  : busy && canStartShift
                    ? "جاري التسجيل..."
                    : "تسجيل حضور"}
              </button>
              {showMiddayPunches && (
                <>
                  <button
                    type="button"
                    className="remote-att-btn remote-att-btn-break-out"
                    disabled={actionsDisabled || !canBreakOut}
                    onClick={handleBreakOut}
                  >
                    {locating && canBreakOut
                      ? "جاري تحديد الموقع..."
                      : busy && canBreakOut
                        ? "جاري التسجيل..."
                        : exitPunchLabel}
                  </button>
                  <button
                    type="button"
                    className="remote-att-btn remote-att-btn-break-in"
                    disabled={actionsDisabled || !canBreakIn}
                    onClick={handleBreakIn}
                  >
                    {locating && canBreakIn
                      ? "جاري تحديد الموقع..."
                      : busy && canBreakIn
                        ? "جاري التسجيل..."
                        : returnPunchLabel}
                  </button>
                </>
              )}
              <button
                type="button"
                className="remote-att-btn remote-att-btn-out"
                disabled={
                  actionsDisabled ||
                  !canCheckOut ||
                  checkoutBlocked
                }
                onClick={handleCheckOut}
              >
                {locating && canCheckOut
                  ? "جاري تحديد الموقع..."
                  : busy && canCheckOut
                    ? "جاري التسجيل..."
                    : checkoutBlocked
                      ? "بانتظار استلام الشيفت"
                      : showMiddayPunches &&
                          checkedIn &&
                          !checkedOut &&
                          !canCheckOut
                        ? middayIsPermit
                          ? "سجّل رجوع الإذن أولاً"
                          : "سجّل البريك أولاً"
                        : "تسجيل انصراف"}
              </button>
            </div>
          </>
        )}

        {isHybrid && !loading && !hybridDayAllowed && (
          <div className="remote-att-actions remote-att-actions-locked">
            <button
              type="button"
              className="remote-att-btn remote-att-btn-in"
              disabled
            >
              تسجيل حضور
            </button>
            {showMiddayPunches && (
              <>
                <button
                  type="button"
                  className="remote-att-btn remote-att-btn-break-out"
                  disabled
                >
                  {exitPunchLabel}
                </button>
                <button
                  type="button"
                  className="remote-att-btn remote-att-btn-break-in"
                  disabled
                >
                  {returnPunchLabel}
                </button>
              </>
            )}
            <button
              type="button"
              className="remote-att-btn remote-att-btn-out"
              disabled
            >
              تسجيل انصراف
            </button>
            <p className="remote-att-locked-hint">
              الأزرار مغلقة اليوم — افتحها فقط بعد موافقة الـ HR على طلب العمل من
              المنزل لهذا اليوم.
            </p>
          </div>
        )}

        {error && <div className="remote-att-error">{error}</div>}
        {success && <div className="remote-att-success">{success}</div>}
      </div>
    </div>
  );
}

export default RemoteAttendance;
